import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, Plus, Trash2, UserPlus, Building2, Briefcase, Layers, Pencil } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useFeature } from "@/lib/auth";
import { PageShell, PageStat } from "@/components/page-shell";

type GroupSummary = { id: string; name: string; description: string | null; memberCount: number; createdAt: string; source?: string | null; externalId?: string | null };
type GroupDetail = GroupSummary & { members: { id: string; email: string; name: string; role: string; department: string | null }[] };
type OrgUser = { id: string; email: string; name: string; role: string; department: string | null; jobTitle: string | null; active: string };
type Department = { id: string; name: string; description: string | null; externalId: string | null };

const PLAN_ERROR_HINT =
  "Your current plan doesn't include Workforce groups. Upgrade your plan (or ask the platform admin to enable the Workforce capability) to use this feature.";

function friendlyError(e: any): string {
  const msg = String(e?.message ?? e ?? "");
  return msg.includes("PLAN_FEATURE_REQUIRED") ? PLAN_ERROR_HINT : msg;
}

export default function GroupsPage() {
  const { toast } = useToast();
  const workforceIncluded = useFeature("workforce");
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newExternalId, setNewExternalId] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [memberPick, setMemberPick] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editExternalId, setEditExternalId] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deptName, setDeptName] = useState("");
  const [deptDesc, setDeptDesc] = useState("");
  const [editDept, setEditDept] = useState<Department | null>(null);
  const [editDeptName, setEditDeptName] = useState("");
  const [editDeptDesc, setEditDeptDesc] = useState("");
  const [deleteDeptTarget, setDeleteDeptTarget] = useState<Department | null>(null);

  const { data: groups, isLoading: groupsLoading } = useQuery<GroupSummary[]>({ queryKey: ["/api/groups"], enabled: workforceIncluded });
  const { data: depts } = useQuery<Department[]>({ queryKey: ["/api/departments"], enabled: workforceIncluded });
  const { data: users } = useQuery<OrgUser[]>({ queryKey: ["/api/org/users"], enabled: workforceIncluded });
  const { data: groupDetail, isLoading: detailLoading, error: detailError } = useQuery<GroupDetail>({
    queryKey: ["/api/groups", selectedGroupId],
    enabled: workforceIncluded && !!selectedGroupId,
  });

  const createGroup = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/groups", {
        name: newName,
        description: newDesc,
        externalId: newExternalId.trim() || null,
      });
      return res.json();
    },
    onSuccess: (g: GroupSummary) => {
      queryClient.invalidateQueries({ queryKey: ["/api/groups"] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups/list"] });
      setCreateOpen(false);
      setNewName(""); setNewDesc(""); setNewExternalId("");
      setSelectedGroupId(g.id);
      toast({ title: "Group created" });
    },
    onError: (e: any) => toast({ title: "Couldn't create group", description: friendlyError(e), variant: "destructive" }),
  });

  const updateGroup = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", `/api/groups/${selectedGroupId}`, {
        name: editName,
        description: editDesc || null,
        externalId: editExternalId || null,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/groups"] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups", selectedGroupId] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups/list"] });
      setEditOpen(false);
      toast({ title: "Group updated" });
    },
    onError: (e: any) => toast({ title: "Couldn't update group", description: friendlyError(e), variant: "destructive" }),
  });

  const restoreGroup = async (id: string) => {
    try {
      await apiRequest("POST", `/api/groups/${id}/restore`);
      queryClient.invalidateQueries({ queryKey: ["/api/groups"] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups/list"] });
      toast({ title: "Group restored" });
    } catch (e: any) {
      toast({ title: "Restore failed", description: friendlyError(e), variant: "destructive" });
    }
  };

  const deleteGroup = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/groups/${id}`),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["/api/groups"] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups/list"] });
      setDeleteConfirmOpen(false);
      setSelectedGroupId(null);
      toast({
        title: "Group archived",
        action: (
          <ToastAction altText="Undo — restore archived group" onClick={() => restoreGroup(id)} data-testid="button-undo-archive-group">
            Undo
          </ToastAction>
        ),
      });
    },
    onError: (e: any) => {
      setDeleteConfirmOpen(false);
      toast({ title: "Couldn't archive group", description: friendlyError(e), variant: "destructive" });
    },
  });

  const openEdit = () => {
    if (!groupDetail) return;
    setEditName(groupDetail.name);
    setEditDesc(groupDetail.description || "");
    setEditExternalId(groupDetail.externalId || "");
    setEditOpen(true);
  };

  const addMember = useMutation({
    mutationFn: async (userId: string) => apiRequest("POST", `/api/groups/${selectedGroupId}/members`, { userId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/groups", selectedGroupId] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups"] });
      setMemberPick("");
      toast({ title: "Member added" });
    },
    onError: (e: any) => toast({ title: "Failed", description: friendlyError(e), variant: "destructive" }),
  });

  const removeMember = useMutation({
    mutationFn: async (userId: string) => apiRequest("DELETE", `/api/groups/${selectedGroupId}/members/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/groups", selectedGroupId] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups"] });
      toast({ title: "Member removed" });
    },
  });

  const createDept = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/departments", { name: deptName, description: deptDesc });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/departments"] });
      queryClient.invalidateQueries({ queryKey: ["/api/departments/list"] });
      setDeptName(""); setDeptDesc("");
      toast({ title: "Department created" });
    },
    onError: (e: any) => toast({ title: "Failed", description: friendlyError(e), variant: "destructive" }),
  });

  const updateDept = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", `/api/departments/${editDept!.id}`, {
        name: editDeptName.trim(),
        description: editDeptDesc.trim() || null,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/departments"] });
      queryClient.invalidateQueries({ queryKey: ["/api/departments/list"] });
      setEditDept(null);
      toast({ title: "Department updated" });
    },
    onError: (e: any) => toast({ title: "Couldn't update department", description: friendlyError(e), variant: "destructive" }),
  });

  const restoreDept = async (id: string) => {
    try {
      await apiRequest("POST", `/api/departments/${id}/restore`);
      queryClient.invalidateQueries({ queryKey: ["/api/departments"] });
      queryClient.invalidateQueries({ queryKey: ["/api/departments/list"] });
      toast({ title: "Department restored" });
    } catch (e: any) {
      toast({ title: "Restore failed", description: friendlyError(e), variant: "destructive" });
    }
  };

  const deleteDept = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/departments/${id}`),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["/api/departments"] });
      queryClient.invalidateQueries({ queryKey: ["/api/departments/list"] });
      setDeleteDeptTarget(null);
      toast({
        title: "Department archived",
        action: (
          <ToastAction altText="Undo — restore archived department" onClick={() => restoreDept(id)} data-testid="button-undo-archive-dept">
            Undo
          </ToastAction>
        ),
      });
    },
    onError: (e: any) => {
      setDeleteDeptTarget(null);
      toast({ title: "Couldn't archive department", description: friendlyError(e), variant: "destructive" });
    },
  });

  const eligibleUsers = users?.filter(u => !groupDetail?.members.some(m => m.id === u.id)) ?? [];

  const totalMembers = groups?.reduce((s, g) => s + (g.memberCount || 0), 0) ?? 0;

  // Plan gate: if the org's plan doesn't include the Workforce capability,
  // show an upgrade notice instead of a form that the server will reject
  // with PLAN_FEATURE_REQUIRED. (Handles stale sessions after a plan change.)
  if (!workforceIncluded) {
    return (
      <PageShell
        eyebrow="Access control"
        title={<span className="flex items-center gap-2" data-testid="heading-groups"><Users className="w-6 h-6" /> Workforce</span>}
        subtitle="Manage groups and departments to control which staff can use which bots."
      >
        <Card data-testid="card-workforce-upgrade">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Layers className="w-5 h-5" /> Not included in your plan</CardTitle>
            <CardDescription>{PLAN_ERROR_HINT}</CardDescription>
          </CardHeader>
        </Card>
      </PageShell>
    );
  }

  return (
    <PageShell
      eyebrow="Access control"
      title={<span className="flex items-center gap-2" data-testid="heading-groups"><Users className="w-6 h-6" /> Workforce</span>}
      subtitle="Manage groups and departments to control which staff can use which bots."
      stats={
        <>
          <PageStat label="Groups" value={groups?.length ?? 0} hint="Bot access sets" icon={<Layers className="w-4 h-4" />} />
          <PageStat label="Departments" value={depts?.length ?? 0} hint="From SSO/SCIM" icon={<Building2 className="w-4 h-4" />} />
          <PageStat label="Members assigned" value={totalMembers} hint="Across all groups" icon={<Users className="w-4 h-4" />} />
          <PageStat label="Org users" value={users?.length ?? 0} hint="Total in directory" icon={<Briefcase className="w-4 h-4" />} />
        </>
      }
    >
      <Tabs defaultValue="groups" className="w-full">
        <TabsList>
          <TabsTrigger value="groups" data-testid="tab-groups">Groups</TabsTrigger>
          <TabsTrigger value="departments" data-testid="tab-departments">Departments</TabsTrigger>
        </TabsList>

        <TabsContent value="groups">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
            <Card className="lg:col-span-1">
              <CardHeader className="flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base">All Groups</CardTitle>
                <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="button-new-group"><Plus className="w-4 h-4 mr-1" />New</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create Group</DialogTitle>
                      <DialogDescription>Groups let you grant a set of users access to specific bots.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="input-group-name">Name</Label>
                        <Input id="input-group-name" value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. HR Staff" data-testid="input-group-name" />
                      </div>
                      <div>
                        <Label htmlFor="input-group-desc">Description (optional)</Label>
                        <Textarea id="input-group-desc" value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="What this group is for" data-testid="input-group-desc" />
                      </div>
                      <div>
                        <Label htmlFor="input-group-external-id">External ID (optional)</Label>
                        <Input id="input-group-external-id" value={newExternalId} onChange={e => setNewExternalId(e.target.value)} placeholder="e.g. SCIM/SSO identifier" data-testid="input-group-external-id" />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
                      <Button onClick={() => createGroup.mutate()} disabled={!newName || createGroup.isPending} data-testid="button-create-group">
                        {createGroup.isPending ? "Creating..." : "Create"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent className="p-2">
                {groupsLoading ? (
                  <div className="space-y-2 p-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>
                ) : !groups || groups.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground text-center">No groups yet. Create one to organize staff.</p>
                ) : (
                  <div className="space-y-1">
                    {groups.map(g => (
                      <button
                        key={g.id}
                        onClick={() => setSelectedGroupId(g.id)}
                        className={`w-full text-left p-3 rounded-md transition-colors ${selectedGroupId === g.id ? "bg-primary/10 border border-primary/20" : "hover:bg-muted"}`}
                        data-testid={`group-item-${g.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{g.name}</span>
                          <Badge variant="outline" className="text-[10px]">{g.memberCount}</Badge>
                        </div>
                        {g.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{g.description}</p>}
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader className="flex-row items-start justify-between space-y-0 gap-2">
                <div className="min-w-0">
                  <CardTitle className="text-base" data-testid="text-selected-group-name">{groupDetail?.name || "Select a group"}</CardTitle>
                  {groupDetail?.description && <CardDescription>{groupDetail.description}</CardDescription>}
                </div>
                {groupDetail && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={openEdit} data-testid="button-edit-group">
                      <Pencil className="w-4 h-4 mr-1" />Edit
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => setDeleteConfirmOpen(true)} data-testid="button-delete-group">
                      <Trash2 className="w-4 h-4 mr-1" />Archive
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {!selectedGroupId ? (
                  <p className="text-sm text-muted-foreground">Pick a group on the left to manage its members.</p>
                ) : detailError ? (
                  <p className="text-sm text-destructive" data-testid="text-group-detail-error">
                    Couldn't load this group: {friendlyError(detailError)}
                  </p>
                ) : detailLoading || !groupDetail ? (
                  <Skeleton className="h-32 w-full" />
                ) : (
                  <>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 mb-4 text-xs text-muted-foreground" data-testid="group-detail-meta">
                      <span>Source: <span className="capitalize">{groupDetail.source || "manual"}</span></span>
                      <span>External ID: {groupDetail.externalId || "—"}</span>
                      {groupDetail.createdAt && <span>Created: {new Date(groupDetail.createdAt).toLocaleDateString()}</span>}
                      <span>{groupDetail.members.length} member{groupDetail.members.length !== 1 ? "s" : ""}</span>
                    </div>
                    <div className="flex items-end gap-2 mb-4">
                      <div className="flex-1">
                        <Label htmlFor="select-add-member">Add member</Label>
                        <Select value={memberPick} onValueChange={setMemberPick}>
                          <SelectTrigger id="select-add-member" data-testid="select-add-member">
                            <SelectValue placeholder={eligibleUsers.length === 0 ? "All org users are already members" : "Pick a user…"} />
                          </SelectTrigger>
                          <SelectContent>
                            {eligibleUsers.map(u => (
                              <SelectItem key={u.id} value={u.id} data-testid={`option-user-${u.id}`}>
                                {u.name} ({u.email})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button onClick={() => memberPick && addMember.mutate(memberPick)} disabled={!memberPick || addMember.isPending} data-testid="button-add-member">
                        <UserPlus className="w-4 h-4 mr-1" />Add
                      </Button>
                    </div>

                    <div className="border rounded-md divide-y">
                      {groupDetail.members.length === 0 ? (
                        <p className="p-4 text-sm text-muted-foreground text-center">No members yet.</p>
                      ) : (
                        groupDetail.members.map(m => (
                          <div key={m.id} className="flex items-center justify-between p-3" data-testid={`member-row-${m.id}`}>
                            <div>
                              <p className="text-sm font-medium">{m.name}</p>
                              <p className="text-xs text-muted-foreground">{m.email} · <span className="capitalize">{(m.role || "member").replace(/_/g, " ")}</span>{m.department ? ` · ${m.department}` : ""}</p>
                            </div>
                            <IconButton variant="ghost" label={`Remove member ${m.name}`} onClick={() => removeMember.mutate(m.id)} data-testid={`button-remove-member-${m.id}`}>
                              <Trash2 className="w-4 h-4" aria-hidden="true" />
                            </IconButton>
                          </div>
                        ))
                      )}
                    </div>

                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Edit group dialog — pre-filled with the selected group's values */}
          <Dialog open={editOpen} onOpenChange={setEditOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Group</DialogTitle>
                <DialogDescription>Update the group's name, description or external ID.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="input-edit-group-name">Name</Label>
                  <Input id="input-edit-group-name" value={editName} onChange={e => setEditName(e.target.value)} data-testid="input-edit-group-name" />
                </div>
                <div>
                  <Label htmlFor="input-edit-group-desc">Description (optional)</Label>
                  <Textarea id="input-edit-group-desc" value={editDesc} onChange={e => setEditDesc(e.target.value)} data-testid="input-edit-group-desc" />
                </div>
                <div>
                  <Label htmlFor="input-edit-group-external-id">External ID (optional)</Label>
                  <Input id="input-edit-group-external-id" value={editExternalId} onChange={e => setEditExternalId(e.target.value)} placeholder="e.g. SCIM/SSO identifier" data-testid="input-edit-group-external-id" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setEditOpen(false)}>Cancel</Button>
                <Button onClick={() => updateGroup.mutate()} disabled={!editName.trim() || updateGroup.isPending} data-testid="button-save-group">
                  {updateGroup.isPending ? "Saving..." : "Save changes"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete confirmation dialog */}
          <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Archive group?</DialogTitle>
                <DialogDescription>
                  This archives "{groupDetail?.name}" so it's no longer active and its member assignments no longer apply. The group's history is retained.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                <Button
                  variant="destructive"
                  onClick={() => selectedGroupId && deleteGroup.mutate(selectedGroupId)}
                  disabled={deleteGroup.isPending}
                  data-testid="button-confirm-delete-group"
                >
                  {deleteGroup.isPending ? "Archiving..." : "Archive group"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="departments">
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4" />Departments</CardTitle>
              <CardDescription>Departments are pulled in via SSO/SCIM and can be used as access criteria for bots.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label htmlFor="input-dept-name">Name</Label>
                  <Input id="input-dept-name" value={deptName} onChange={e => setDeptName(e.target.value)} placeholder="e.g. Engineering" data-testid="input-dept-name" />
                </div>
                <div className="flex-1">
                  <Label htmlFor="input-dept-desc">Description</Label>
                  <Input id="input-dept-desc" value={deptDesc} onChange={e => setDeptDesc(e.target.value)} placeholder="optional" data-testid="input-dept-desc" />
                </div>
                <Button onClick={() => createDept.mutate()} disabled={!deptName || createDept.isPending} data-testid="button-create-dept">
                  <Plus className="w-4 h-4 mr-1" />Add
                </Button>
              </div>

              <div className="border rounded-md divide-y">
                {!depts || depts.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground text-center">No departments yet.</p>
                ) : (
                  depts.map(d => {
                    const scimManaged = !!d.externalId;
                    const openDeptEdit = () => {
                      if (scimManaged) return;
                      setEditDept(d);
                      setEditDeptName(d.name);
                      setEditDeptDesc(d.description || "");
                    };
                    return (
                      <div
                        key={d.id}
                        role={scimManaged ? undefined : "button"}
                        tabIndex={scimManaged ? undefined : 0}
                        className={`flex items-center justify-between p-3 ${scimManaged ? "" : "cursor-pointer hover:bg-muted/50"}`}
                        onClick={scimManaged ? undefined : openDeptEdit}
                        onKeyDown={scimManaged ? undefined : (e) => (e.key === "Enter" || e.key === " ") && e.currentTarget === e.target && openDeptEdit()}
                        data-testid={`dept-row-${d.id}`}
                      >
                        <div>
                          <p className="text-sm font-medium flex items-center gap-2">
                            <Briefcase className="w-3 h-3" />{d.name}
                            {scimManaged && <Badge variant="secondary" className="text-[10px]" data-testid={`badge-scim-dept-${d.id}`}>Managed by SSO/SCIM</Badge>}
                          </p>
                          {d.description && <p className="text-xs text-muted-foreground">{d.description}</p>}
                        </div>
                        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          <IconButton variant="ghost" label={`Edit department ${d.name}`} disabled={scimManaged} onClick={openDeptEdit} data-testid={`button-edit-dept-${d.id}`}>
                            <Pencil className="w-4 h-4" aria-hidden="true" />
                          </IconButton>
                          <IconButton variant="ghost" label={`Archive department ${d.name}`} disabled={scimManaged} onClick={() => setDeleteDeptTarget(d)} data-testid={`button-delete-dept-${d.id}`}>
                            <Trash2 className="w-4 h-4" aria-hidden="true" />
                          </IconButton>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>

          <Dialog open={!!editDept} onOpenChange={o => { if (!o) setEditDept(null); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit department</DialogTitle>
                <DialogDescription>Update the department's name and description.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="edit-dept-name">Name</Label>
                  <Input id="edit-dept-name" value={editDeptName} onChange={e => setEditDeptName(e.target.value)} data-testid="input-edit-dept-name" />
                </div>
                <div>
                  <Label htmlFor="edit-dept-desc">Description</Label>
                  <Textarea id="edit-dept-desc" value={editDeptDesc} onChange={e => setEditDeptDesc(e.target.value)} placeholder="optional" data-testid="input-edit-dept-desc" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditDept(null)} data-testid="button-cancel-edit-dept">Cancel</Button>
                <Button
                  onClick={() => updateDept.mutate()}
                  disabled={!editDeptName.trim() || updateDept.isPending}
                  data-testid="button-save-edit-dept"
                >
                  {updateDept.isPending ? "Saving..." : "Save changes"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={!!deleteDeptTarget} onOpenChange={o => { if (!o) setDeleteDeptTarget(null); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Archive department?</DialogTitle>
                <DialogDescription>
                  This archives "{deleteDeptTarget?.name}" so it's no longer active. Its history is retained.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDeleteDeptTarget(null)} data-testid="button-cancel-delete-dept">Cancel</Button>
                <Button
                  variant="destructive"
                  onClick={() => deleteDept.mutate(deleteDeptTarget!.id)}
                  disabled={deleteDept.isPending}
                  data-testid="button-confirm-delete-dept"
                >
                  {deleteDept.isPending ? "Archiving..." : "Archive department"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </PageShell>
  );
}
